// Components
import { Checkbox, Select } from "@mantine/core";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { FormSection } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { PractitionerRoleSearchSelect } from "@benchmark/ui/components/atomic";

// Constants
import { getEmrSystemOptions } from "@benchmark/core/constants";

// Types
import type { UseFormReturnType } from "@mantine/form";
import type { PatientFormData } from "@benchmark/core/types";

/**
 * Props for ReferralInformationSection component.
 * @agentic connections
 */
type ReferralInformationSectionProps = {
  form: UseFormReturnType<PatientFormData>;
  partnerPractitionerRoleOptions: Array<{ value: string; label: string }>;
  loadingPartnerPractitioners: boolean;
  existingPatient?: boolean;
  emrOptions?: Array<{ value: string; label: string }>;
  pcpSearchQuery?: string;
  onPcpSearchChange?: (query: string) => void;
  onPcpSelect?: (value: string | null) => void;
};

/**
 * Form section for referral info (PCP, referral date, EMR, MRN, CoCM consent).
 * @agentic connections
 */
export const ReferralInformationSection = ({
  form,
  partnerPractitionerRoleOptions,
  loadingPartnerPractitioners,
  existingPatient = false,
  emrOptions = getEmrSystemOptions(),
  pcpSearchQuery = "",
  onPcpSearchChange,
  onPcpSelect,
}: ReferralInformationSectionProps) => {
  const referringPractice = form.values["referring-practice"] as
    string | undefined;
  const referringPractitioner = form.values["referring-practitioner"] as
    string | undefined;

  return (
    <FormSection title="Referral Information">
      {(referringPractice || referringPractitioner) && (
        <FormGroup>
          {referringPractice && (
            <FormField label="Referring Practice (extracted from fax)">
              <TextInput
                value={referringPractice}
                readOnly
                variant="filled"
                {...form.getInputProps("referring-practice")}
              />
            </FormField>
          )}
          {referringPractitioner && (
            <FormField label="Referring Practitioner (extracted from fax)">
              <TextInput
                value={referringPractitioner}
                readOnly
                variant="filled"
                {...form.getInputProps("referring-practitioner")}
              />
            </FormField>
          )}
        </FormGroup>
      )}

      <FormGroup>
        <FormField label="Primary Care Provider" required>
          <PractitionerRoleSearchSelect
            options={partnerPractitionerRoleOptions}
            value={(form.values["primary-care-provider"] as string) || null}
            searchQuery={pcpSearchQuery}
            isSearching={loadingPartnerPractitioners}
            onSearchChange={onPcpSearchChange}
            onChange={
              onPcpSelect ||
              form.getInputProps("primary-care-provider").onChange
            }
            placeholder="Search referring providers..."
            noResultsMessage="No providers found"
            error={form.errors["primary-care-provider"]}
          />
        </FormField>
        <FormField label="Referral Date" required>
          <TextInput
            // TODO(#981): Abstract a shared DateSelect to standardize date UX
            type="date"
            autoComplete="off"
            disabled={!!existingPatient}
            {...form.getInputProps("referral-date")}
          />
        </FormField>
      </FormGroup>

      <FormGroup>
        <FormField label="EMR" required>
          <Select
            placeholder="Select EMR system"
            data={emrOptions}
            searchable
            clearable
            {...form.getInputProps("emr")}
          />
        </FormField>
        <FormField label="MRN" required>
          <TextInput
            placeholder="Enter MRN"
            autoComplete="off"
            {...form.getInputProps("mrn")}
          />
        </FormField>
      </FormGroup>

      {existingPatient && (
        <FormGroup>
          <FormField label="CoCM Consent Verified">
            <Checkbox
              label="Consent Verified"
              {...form.getInputProps("cocm-consent-verified", {
                type: "checkbox",
              })}
            />
          </FormField>
          <FormField label="CoCM Consent Date">
            <TextInput
              type="date"
              autoComplete="off"
              {...form.getInputProps("cocm-consent-date")}
            />
          </FormField>
        </FormGroup>
      )}
    </FormSection>
  );
};
